import { z } from 'zod'
import { DASHBOARD_DATE_PRESETS, presetDays } from '~/components/dashboards/filters/date-presets'
import type { DateRangePreset } from '~/components/ui/date-range-picker'
import { frenchDateOfIso } from '~/lib/iso-date'
import { pluralize } from '~/lib/utils'
import {
  addDays,
  type DateRange,
  isFullMonth,
  isIsoWeek,
  monthYearLabel,
  parseDay,
  rangeDays,
  rangeText,
  toIso,
  weekNumberOf,
  yearOf,
} from './date-range-model'
import { recentsStore } from './filter-recents'

export const PERIOD_PRESETS = [
  'yesterday',
  '7d',
  '30d',
  '3m',
  '12m',
  'mtd',
  'ytd',
  'all',
] as const satisfies readonly DateRangePreset[]

export type PeriodPreset = (typeof PERIOD_PRESETS)[number]

export const DEFAULT_PERIOD_PRESET: PeriodPreset = '7d'

export const PERIOD_PRESET_LABELS = Object.fromEntries(
  DASHBOARD_DATE_PRESETS.map(({ value, label }) => [
    value,
    value === 'ytd' ? 'Depuis le 1er janvier' : label,
  ]),
) as Record<PeriodPreset, string>

export const LEGACY_WEEK_PRESETS = [
  'last_week',
  'week_minus_2',
  'week_minus_3',
  'last_4_weeks',
  'current_week',
] as const

export type LegacyWeekPreset = (typeof LEGACY_WEEK_PRESETS)[number]

export interface Period extends DateRange {
  preset?: PeriodPreset
}

export const latestDay = (today: string): string => addDays(today, -1)

export function periodPresetRange(
  preset: PeriodPreset,
  today: string,
  allTimeStart: string | undefined,
): DateRange {
  const { start, end } = presetDays(preset, parseDay(today))
  const startDate = start ?? allTimeStart ?? end
  return { startDate: startDate < end ? startDate : end, endDate: end }
}

const LEGACY_PAST_WEEKS: Record<Exclude<LegacyWeekPreset, 'current_week'>, [number, number]> = {
  last_week: [1, 1],
  week_minus_2: [2, 1],
  week_minus_3: [3, 1],
  last_4_weeks: [4, 4],
}

export function legacyWeekPresetRange(preset: LegacyWeekPreset, today: string): DateRange {
  const thisMonday = parseDay(today).startOf('week')
  if (preset === 'current_week') {
    const yesterday = parseDay(latestDay(today))
    return {
      startDate: toIso(yesterday < thisMonday ? yesterday : thisMonday),
      endDate: toIso(yesterday),
    }
  }
  const [weeksAgo, weekCount] = LEGACY_PAST_WEEKS[preset]
  const start = thisMonday.minus({ weeks: weeksAgo })
  return {
    startDate: toIso(start),
    endDate: toIso(start.plus({ weeks: weekCount, days: -1 })),
  }
}

export interface PanelLabel {
  title: string
  detail?: string
  muted?: boolean
}

function customTitle(range: DateRange, year: number): string {
  if (isFullMonth(range)) return monthYearLabel(range.startDate)
  if (isIsoWeek(range)) return `S${weekNumberOf(range.startDate)}`
  return rangeText(range, { year })
}

export function periodButtonLabel(period: Period, today: string): PanelLabel {
  const year = yearOf(today)
  const detail = rangeText(period, { year })
  const title = period.preset ? PERIOD_PRESET_LABELS[period.preset] : customTitle(period, year)
  return title === detail ? { title } : { title, detail }
}

export function periodSummary(period: Period | null, today: string): PanelLabel {
  if (period === null) return { title: 'Plage invalide', muted: true }
  return {
    title: rangeText(period, { year: yearOf(today) }),
    detail: period.preset ? PERIOD_PRESET_LABELS[period.preset] : 'Plage libre',
  }
}

const recentSchema = z.union([
  z.object({ preset: z.enum(PERIOD_PRESETS), at: z.string() }),
  z.object({ startDate: z.string(), endDate: z.string(), at: z.string() }),
])

export type PeriodRecent = z.infer<typeof recentSchema>

export const periodRecents = recentsStore<PeriodRecent>({
  key: 'gozoki.network-dashboard.period-picker.recents.v1',
  schema: recentSchema,
  max: 2,
  identity: (entry) => ('preset' in entry ? entry.preset : `${entry.startDate}|${entry.endDate}`),
})

export const periodRecentOf = (period: Period, at: string): PeriodRecent =>
  period.preset
    ? { preset: period.preset, at }
    : { startDate: period.startDate, endDate: period.endDate, at }

export const periodOfRecent = (
  recent: PeriodRecent,
  today: string,
  allTimeStart: string | undefined,
): Period =>
  'preset' in recent
    ? { ...periodPresetRange(recent.preset, today, allTimeStart), preset: recent.preset }
    : { startDate: recent.startDate, endDate: recent.endDate }

export function periodRecentRow(period: Period, today: string): { title: string; meta: string } {
  const year = yearOf(today)
  if (period.preset) {
    return {
      title: PERIOD_PRESET_LABELS[period.preset],
      meta:
        period.startDate === period.endDate
          ? frenchDateOfIso(period.endDate)
          : rangeText(period, { dash: '-', year }),
    }
  }
  if (isFullMonth(period)) return { title: monthYearLabel(period.startDate), meta: 'Mois complet' }
  const range = rangeText(period, { dash: '-', year })
  if (isIsoWeek(period)) {
    const week = weekNumberOf(period.startDate)
    return { title: `S${week} - ${range}`, meta: `Semaine ${week}` }
  }
  return { title: range, meta: `Plage de ${pluralize(rangeDays(period), 'jour')}` }
}
